import { ActionAccess, canPerform, NO_ACTION_ACCESS } from './actionGate';

export const checkPermission = (routeRole: any, userRole: any) => {
  if (!routeRole || !routeRole) {
    return true;
  }

  if (userRole && Array.isArray(userRole) && !Array.isArray(routeRole)) {
    return userRole.indexOf(routeRole) >= 0;
  }
  if (routeRole.length === 0) {
    return !userRole || userRole.length === 0;
  }
  if (userRole && Array.isArray(userRole) && Array.isArray(routeRole)) {
    return routeRole.some((r) => userRole.indexOf(r) >= 0);
  }
  return routeRole.indexOf(userRole) >= 0;
};

export type PermissionMapEntry = {
  policies?: string[];
  noCheckPermissionWith?: string[];
  allowedRoles?: string[];
  actions?: string[];
  companyLevelOnly?: boolean;
};

/**
 * A path as the map keys it: with a leading slash, and in lower case, as the
 * router matches a path whatever its case.
 */
const normalizePath = (path: string) =>
  (path.startsWith('/') ? path : `/${path}`).toLowerCase();

/**
 * Build a flat map of url → { policies, noCheckPermissionWith } from routeConfig.
 * This allows RouteGenerator to enforce the same permission checks as the menu filter.
 * An item's `aliases` are further paths of its screens that the menu does not
 * link (a second path to the same page, a create or detail page); each gets
 * the item's entry, and so do the paths under it.
 */
export const buildPermissionMap = (
  routeConfig: any[],
): Map<string, PermissionMapEntry> => {
  const map = new Map<string, PermissionMapEntry>();

  // A company-level item hides its whole subtree from the menu, so its
  // children inherit the flag.
  const traverse = (items: any[], parentCompanyLevelOnly = false) => {
    for (const item of items) {
      const companyLevelOnly = parentCompanyLevelOnly || !!item.companyLevelOnly;
      const urls: string[] = [
        ...(item.url ? [item.url] : []),
        ...(item.aliases ?? []),
      ];
      if (
        urls.length &&
        (item.policies || item.noCheckPermissionWith || item.allowedRoles)
      ) {
        const entry: PermissionMapEntry = {};
        if (item.policies) entry.policies = item.policies;
        if (item.noCheckPermissionWith)
          entry.noCheckPermissionWith = item.noCheckPermissionWith;
        if (item.allowedRoles) entry.allowedRoles = item.allowedRoles;
        if (item.actions) entry.actions = item.actions;
        if (companyLevelOnly) entry.companyLevelOnly = true;
        for (const url of urls) map.set(normalizePath(url), entry);
      }
      if (item.children) traverse(item.children, companyLevelOnly);
    }
  };

  traverse(routeConfig);
  return map;
};

/**
 * The permission entry a route path falls under: the one with the longest url
 * the path equals or sits below, or null.
 */
export const findPermissionEntry = (
  path: string,
  permissionMap: Map<string, PermissionMapEntry>,
): PermissionMapEntry | null => {
  const normalizedPath = normalizePath(path);

  let bestMatch: PermissionMapEntry | null = null;
  let bestLength = 0;

  for (const [url, entry] of permissionMap) {
    if (
      (normalizedPath === url || normalizedPath.startsWith(`${url}/`)) &&
      url.length > bestLength
    ) {
      bestMatch = entry;
      bestLength = url.length;
    }
  }
  return bestMatch;
};

/**
 * Check if the user holds a policy a given route path asks for.
 * Matches the route path against the permission map using prefix matching.
 */
export const checkRoutePermission = (
  path: string,
  userRole: any,
  userPolicies: string[],
  permissionMap: Map<string, PermissionMapEntry>,
  access: ActionAccess = NO_ACTION_ACCESS,
  hasFullDepartmentCompany = true,
): boolean => {
  if (!permissionMap || permissionMap.size === 0) return true;

  const bestMatch = findPermissionEntry(path, permissionMap);

  // No permission entry found for this path — allow access
  if (!bestMatch) return true;

  const currentRole = Array.isArray(userRole) ? userRole : [userRole];

  // Role gate — mirror the menu's allowedRoles: if the user's role isn't in
  // the allowed list, deny (menu hides the item for that role). No admin
  // bypass here — admins are subject to permission checks just like the menu.
  if (bestMatch.allowedRoles?.length) {
    const roleAllowed = bestMatch.allowedRoles.some((role) =>
      currentRole.includes(role),
    );
    if (!roleAllowed) return false;
  }

  // Mirror the menu: company-wide output is never filtered to a subset of
  // departments, so a user with no all-department company left gets the
  // Unauthorized page on a typed URL rather than the screen's refused calls.
  if (bestMatch.companyLevelOnly && !hasFullDepartmentCompany) return false;

  // Skip permission check if user's role is in noCheckPermissionWith
  if (bestMatch.noCheckPermissionWith?.length) {
    const shouldSkip = bestMatch.noCheckPermissionWith.some((role) =>
      currentRole.includes(role),
    );
    if (shouldSkip) return true;
  }

  // Any one of the policies the route lists opens it.
  const screenAllowed = bestMatch.policies?.length
    ? bestMatch.policies.some((key) => userPolicies.includes(key))
    : true;

  return bestMatch.actions?.length
    ? canPerform(access, bestMatch.actions, screenAllowed)
    : screenAllowed;
};
